/**
 * The firmware's reply vocabulary: the `data.statustype` of every acknowledgement
 * it can send, plus the closed code sets those replies carry.
 *
 * Taken from the RCU HTTP and MQTT command reference (4.2.0, 3986962). A
 * `statustype` — not the unused `send_command_ack()` parameter — identifies a
 * reply, so this table is what the decoder switches on.
 */

/** `data.statustype` values, keyed by what produced them. */
export const RCU_STATUS_TYPE = {
  /** `rcuoperate` submit result. Shares its spelling with the envelope type. */
  RELAY_ACK: 'rcuack',
  /** Stored relay state, from `getrcurelay` or a physical operation. */
  RELAY_STATUS: 'relaystatus',
  /** All 26 relay states, from `getallrcurelays`. */
  ALL_RELAYS_STATUS: 'allrelaysstatus',
  /** `dinoperate` submit result. */
  DIN_ACK: 'dinack',
  /** One DIN channel, from `getdinstatus` or a physical operation. */
  DIN_STATUS: 'dinstatus',
  /** Every channel of one DIN module, from `getdinstatusall`. */
  DIN_STATUS_ALL: 'dinstatusall',
  /** `dalioperate` submit result. */
  DALI_OPERATE: 'dalioperate',
  /** Bus answer to a DALI operation or query. */
  DALI_ACK: 'daliack',
  /** Panel watch started, an observed operation, or its timeout. */
  LISTEN_PANEL: 'listenpanel',
  /** Service-panel event, not tied to a command. */
  NOTIFY_EVENT: 'notifyevt',
  MAPPING_ACK: 'mappingack',
  MAPPING_ERASE: 'mappingerase',
  MAPPING_ERASE_ALL: 'mappingeraseall',
  LED_MAPPING_ACK: 'ledmappingack',
  LED_MAPPING_ERASE: 'ledmappingerase',
  OTA_PROGRESS: 'otaprogress',
  OTA_ACK: 'otaack',
  LOGGING_ACK: 'loggingack',
  LOGGING_STATUS: 'loggingstatus',
  LOGS_ACK: 'logsack',
  COREDUMP_INFO: 'coredumpinfo',
  COREDUMP_ACK: 'coredumpack',
  S3_PROGRESS: 's3progress',
  S3_MAPPING_ACK: 's3mappingack',
} as const;

export type RcuStatusType = (typeof RCU_STATUS_TYPE)[keyof typeof RCU_STATUS_TYPE];

/** Every reply's `status` field, where it has one. */
export const ACK_STATUS = {
  OK: 'ok',
  FAIL: 'fail',
} as const;

export type AckStatus = (typeof ACK_STATUS)[keyof typeof ACK_STATUS];

/** The raw `status` word a status document carries. */
export const REPORTED_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
} as const;

export type ReportedStatus = (typeof REPORTED_STATUS)[keyof typeof REPORTED_STATUS];

/** How a reply's status reads in a sentence, for the feed and the panels. */
export const ACK_STATUS_LABEL = {
  [ACK_STATUS.OK]: 'acknowledged',
  [ACK_STATUS.FAIL]: 'failed',
} as const satisfies Record<AckStatus, string>;

export const ackStatusLabel = (status: AckStatus): string => ACK_STATUS_LABEL[status];

/** Text a `listenpanel` reply carries instead of an operation. */
export const PANEL_STATUS = {
  OK: 'ok',
  TIMEOUT: 'timeout',
} as const;

export type PanelStatus = (typeof PANEL_STATUS)[keyof typeof PANEL_STATUS];

/** What a panel watch is doing, once a reply has been read. */
export const PANEL_STATE = {
  /** The watch was accepted and is running. */
  STARTED: 'started',
  /** A panel operation was observed. */
  OPERATION: 'operation',
  /** The five-minute window closed. */
  TIMEOUT: 'timeout',
} as const;

export type PanelState = (typeof PANEL_STATE)[keyof typeof PANEL_STATE];

/** Coredump `reset` reasons, as the firmware spells them. */
export const COREDUMP_RESET = {
  POWER_ON: 'POWERON',
  EXTERNAL: 'EXT',
  SOFTWARE: 'SW',
  PANIC: 'PANIC',
  INTERRUPT_WATCHDOG: 'INT_WDT',
  TASK_WATCHDOG: 'TASK_WDT',
  WATCHDOG: 'WDT',
  DEEP_SLEEP: 'DEEPSLEEP',
  BROWNOUT: 'BROWNOUT',
  SDIO: 'SDIO',
  UNKNOWN: 'UNKNOWN',
} as const;

export type CoredumpReset = (typeof COREDUMP_RESET)[keyof typeof COREDUMP_RESET];

/** What a reset reason means for whoever is reading the console. */
export const COREDUMP_RESET_LABEL = {
  [COREDUMP_RESET.POWER_ON]: 'Power on',
  [COREDUMP_RESET.EXTERNAL]: 'External reset',
  [COREDUMP_RESET.SOFTWARE]: 'Software reset',
  [COREDUMP_RESET.PANIC]: 'Panic',
  [COREDUMP_RESET.INTERRUPT_WATCHDOG]: 'Interrupt watchdog',
  [COREDUMP_RESET.TASK_WATCHDOG]: 'Task watchdog',
  [COREDUMP_RESET.WATCHDOG]: 'Watchdog',
  [COREDUMP_RESET.DEEP_SLEEP]: 'Deep sleep wake',
  [COREDUMP_RESET.BROWNOUT]: 'Brownout',
  [COREDUMP_RESET.SDIO]: 'SDIO reset',
  [COREDUMP_RESET.UNKNOWN]: 'Unknown',
} as const satisfies Record<CoredumpReset, string>;

/** Reasons `getcoredump` refuses to start. */
export const COREDUMP_ERROR = {
  NO_COREDUMP: 'no_coredump',
  NOT_CONNECTED: 'not_connected',
  UPLOAD_IN_PROGRESS: 'upload_in_progress',
  UNKNOWN: 'unknown',
} as const;

/** Reasons a mapping import fails before it reads a single entry. */
export const S3_ERROR = {
  MISSING_URL: 'missing_url',
  MEMORY_ERROR: 'memory_error',
  HTTP_INIT_ERROR: 'http_init_error',
  CONNECTION_ERROR: 'connection_error',
  HEADER_ERROR: 'header_error',
  FILE_TOO_LARGE: 'file_too_large',
  DOWNLOAD_ERROR: 'download_error',
  JSON_PARSE_ERROR: 'json_parse_error',
} as const;

/** Copy for the codes above, with the raw code as the fallback. */
const ERROR_LABELS: Readonly<Record<string, string>> = {
  [COREDUMP_ERROR.NO_COREDUMP]: 'No coredump is stored on the RCU.',
  [COREDUMP_ERROR.NOT_CONNECTED]: 'The RCU has no MQTT connection, so it cannot upload.',
  [COREDUMP_ERROR.UPLOAD_IN_PROGRESS]: 'An upload is already running.',
  [COREDUMP_ERROR.UNKNOWN]: 'The RCU refused the request for an unknown reason.',
  [S3_ERROR.MISSING_URL]: 'No URL was sent.',
  [S3_ERROR.MEMORY_ERROR]: 'The RCU could not allocate memory for the file.',
  [S3_ERROR.HTTP_INIT_ERROR]: 'The RCU could not start its HTTPS client.',
  [S3_ERROR.CONNECTION_ERROR]: 'The RCU could not reach the URL.',
  [S3_ERROR.HEADER_ERROR]: 'The response was not usable.',
  [S3_ERROR.FILE_TOO_LARGE]: 'The file is larger than the RCU accepts.',
  [S3_ERROR.DOWNLOAD_ERROR]: 'The download failed part way through.',
  [S3_ERROR.JSON_PARSE_ERROR]: 'The downloaded file is not JSON.',
};

export const errorLabel = (code: string): string => ERROR_LABELS[code] ?? code;
